/**
 * packages/shared/src/verification/presentation.ts
 *
 * Active Verification result PRESENTATION synthesis (SIL-585, spec §9/§13
 * Wave 4). Pure functions only — no DB, no fetch — so the same synthesized
 * shape can be computed server-side (passport assembly, listing embed) and
 * asserted in tests without a live Postgres.
 *
 * "Synthesized onto the passport and listing, in the SAME synthesized form"
 * (§9, the map-owner decision) means exactly one function produces the
 * render-ready state; every surface (passport §-section, listing card,
 * evidence-sources panel) consumes `VerificationPresentation`, never a raw
 * `verification_run` row.
 *
 * Hard anti-laundering requirement carried from §9/§11 finding 6: Tier 3
 * (browser-only, unverified) NEVER produces a `tier12_*` variant — it is a
 * structurally distinct discriminated-union member the renderer cannot
 * accidentally badge like a verified tier. See `TIER_3_SELF_REPORTED_LABEL`.
 */

/** TTL for a qualifying run to still be display-current (§9). Re-exported
 * here (not imported from scoring/verification-confidence.ts) to keep this
 * module dependency-free for the render layer — the two constants are kept
 * in sync by the shared verification-presentation test suite. */
export const VERIFICATION_DISPLAY_TTL_DAYS = 90;

/** Seller-only grace-retry window (§9): a pass within this many ms of a
 * prior fail on the SAME asset renders as "passed on retry", not a bare pass. */
export const GRACE_RETRY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The hard-required, non-badge Tier 3 label (§9/§11 finding 6 — "explicitly
 * labeled 'seller self-reported, not verified by SiliconX'"). Exported as a
 * constant so every renderer uses the exact same string, verbatim.
 */
export const TIER_3_SELF_REPORTED_LABEL = 'Seller self-reported, not verified by SiliconX';

export type VerificationRunTier = 1 | 2 | 3;

/**
 * SIL-613 — presentation shape for the two independent checks. Deliberately
 * carried as its OWN structure, never folded into a combined pass/fail
 * field: the product decision is "no aggregate badge" (see
 * `COMPUTE_CORRECTNESS_*`/`vramLabel` below) — every renderer that reads
 * `.checks` gets two separate results, never a rollup this module could
 * compute for them by mistake.
 *
 * `performed: false` means the check never ran for the latest run (an
 * early-exit POST — no adapter / software adapter — or a pre-SIL-613 row
 * with no `checksPerformed` data at all); `passed` and `bytesTested` are
 * meaningless in that case and are always reported as `false`/`0` rather
 * than left ambiguous, so a renderer branching only on `passed` can never
 * accidentally show a false "pass".
 */
export interface VerificationChecksPresentation {
  compute: { performed: boolean; passed: boolean };
  vram: { performed: boolean; passed: boolean; bytesTested: number };
}

/**
 * SIL-688 — honest multi-GPU presentation shape. `measuredIndex: null`
 * means the harness could not disambiguate which of `devices` WebGPU
 * actually measured (see ingest-schema.ts's `verificationHardwareInfoSchema`
 * doc comment) — the renderer must show "measured 1 of N (unknown which)"
 * in that case, never guess index 0. `devices` carries every enumerated
 * accelerator, serial included; a `null` serial means the vendor tool was
 * absent or the part exposes none (fail-open probe, never an error).
 */
/**
 * SIL-689 — per-device loop outcome + verdict, alongside SIL-688's identity
 * fields. `status` mirrors `verificationDeviceLoopStatusSchema`
 * (ingest-schema.ts) — 'measured' | 'not_run' | 'timeout' | 'error' |
 * 'pin_mismatch' | 'interrupted'; `undefined` on a pre-SIL-689 row (no loop
 * ran, the single legacy measurement is described by the parent
 * `VerificationRunForPresentation` fields instead). `verdict` is
 * server-computed at ingest time (never client-posted) and present only
 * when `status === 'measured'`.
 */
export interface VerificationHardwarePresentation {
  gpuCount: number;
  measuredIndex: number | null;
  devices: {
    index: number;
    vendor: string;
    name?: string;
    serial: string | null;
    status?: string;
    verdict?: string | null;
    errorReason?: string | null;
  }[];
}

/** The subset of a verification_run row the presentation layer needs — one
 * row per completed run, most-recent-first order not required (this module
 * sorts). */
export interface VerificationRunForPresentation {
  tier: VerificationRunTier;
  status: string;
  verdict: string | null;
  completedAt: Date | null;
  /** SIL-613 — {compute, vram} booleans; null/undefined for a pre-SIL-613 row (treated as neither check having run). */
  checksPerformed?: { compute: boolean; vram: boolean } | null;
  computeCorrectness?: { passed: boolean } | null;
  vramTest?: { passed: boolean; bytesTested: number } | null;
  /** SIL-688 — null for a run posted before v8 (no probe ran) or a run
   * whose host only ever had one GPU probed; the renderer treats null as
   * "no multi-GPU caveat to show", never as "zero GPUs". */
  hardware?: VerificationHardwarePresentation | null;
}

export type VerificationPresentation =
  | { kind: 'none' }
  | {
      kind: 'tier12_pass';
      tier: 1 | 2;
      verdict: 'pass' | 'pass_with_observations';
      completedAt: string;
      /** TTL-expired — still shown (§9: "run date always displayed"), never hidden. */
      stale: boolean;
      expiresAt: string;
      passedOnRetry: boolean;
      checks: VerificationChecksPresentation;
      hardware: VerificationHardwarePresentation | null;
    }
  | {
      kind: 'tier12_failed';
      tier: 1 | 2;
      /** Raw verdict classification, e.g. `fail:thermal_throttle` — the
       * literal "verification failed: <classification>" flag text (§9). */
      classification: string;
      completedAt: string;
      checks: VerificationChecksPresentation;
      hardware: VerificationHardwarePresentation | null;
    }
  | {
      kind: 'tier12_flagged';
      tier: 1 | 2;
      classification: string;
      completedAt: string;
      checks: VerificationChecksPresentation;
      hardware: VerificationHardwarePresentation | null;
    }
  | {
      /** Tier 3 — ALWAYS this variant, pass or fail. Never `tier12_*`. */
      kind: 'tier3_self_reported';
      passed: boolean;
      verdict: string;
      completedAt: string;
      checks: VerificationChecksPresentation;
      hardware: VerificationHardwarePresentation | null;
    };

/**
 * Symptom-first, neutral-cause copy per §7 verdict classification (Voice
 * rule: consequence-first, never an asserted root cause or repair advice —
 * "thermal throttling observed", never "fan failure").
 */
const FAILURE_SYMPTOM_COPY: Record<string, string> = {
  'fail:thermal_throttle': 'thermal throttling observed during the test',
  'fail:power_cap_transition': 'a power-cap transition observed during the test',
  'fail:low_baseline': 'throughput below the expected baseline for this hardware',
  'fail:high_jitter': 'high latency variability during the test',
  'fail:device_lost': 'the device disconnected during the test',
  'fail:compute_mismatch': 'incorrect arithmetic results during the compute stress test (silent data corruption)',
  'fail:vram_mismatch': 'a VRAM pattern-test failure in the sampled memory region',
  'flag:identity_mismatch': 'the reported hardware identity could not be confirmed',
  // SIL-586/§7 — distinct from flag:identity_mismatch (roofline
  // plausibility): this is the Tier 1 BMC-attested-serial identity signal.
  'flag:serial_mismatch': 'the attested hardware serial did not match the listed asset',
  invalid_run: 'the test could not complete on a verified hardware adapter',
};

/** Neutral, symptom-first sentence for a failure/flag classification —
 * never a root cause, never repair advice. Falls back to a generic neutral
 * sentence for a future classification this table hasn't been updated for
 * yet (the vocabulary is expected to grow, §7). */
export function verificationFailureSymptom(classification: string): string {
  return FAILURE_SYMPTOM_COPY[classification] ?? 'the test did not complete successfully';
}

function isFailVerdict(verdict: string | null): boolean {
  return verdict?.startsWith('fail:') ?? false;
}

function isFlagVerdict(verdict: string | null): boolean {
  return verdict?.startsWith('flag:') ?? false;
}

/**
 * The three verdict FAMILIES a buyer-facing surface renders. Distinct from a
 * raw `verification_run.verdict`, which is a fine-grained CLASSIFICATION
 * (`fail:thermal_throttle`, `flag:serial_mismatch`, …) and matches none of
 * these strings.
 */
export type VerificationVerdictFamily = 'pass' | 'pass_with_observations' | 'fail';

/**
 * SIL-1366 — collapse a RAW `verification_run.verdict` onto its family.
 *
 * Every surface that renders a verdict badge must go through this. A raw value
 * is `pass`, `pass_with_observations`, `invalid_run`, or a prefixed
 * classification (`fail:*` / `flag:*`), so a lookup keyed on the raw string
 * against a three-entry family table silently misses EVERY failure and falls
 * through to whatever the caller's not-found branch does. In
 * `LotContentsTable` that branch was the neutral em dash, i.e. a buyer read a
 * FAIL as "no run in the window" — the exact confusion the third AV state
 * (SIL-1345) exists to prevent, and pointing the wrong way.
 *
 * `null` is the NEUTRAL family — genuinely no verdict to render — and it is
 * deliberately what an unrecognised classification returns too: the failure
 * families are matched by PREFIX, so any future `fail:*` is already a `fail`
 * here, and anything left over is not a failure this function may invent.
 *
 * `flag:*` maps to `pass_with_observations`, never `fail`: a flag is a signal
 * worth disclosing on a run with no failures (matching `tier12_flagged` in
 * `synthesizeVerificationPresentation` and `nodeVerdictId` in the SIL-1349
 * drawer). Rendering it in the failure palette would overstate it.
 *
 * `invalid_run` maps to `null` for the same reason it falls through to `none`
 * in the synthesizer below: a run that could not complete is neither a pass
 * nor a fail.
 *
 * Already-normalised input is a fixed point (`'fail' as a family stays
 * 'fail'`), so a caller holding a family rather than a raw verdict can pass it
 * through this function safely.
 */
export function verificationVerdictFamily(
  verdict: string | null | undefined,
): VerificationVerdictFamily | null {
  if (verdict === null || verdict === undefined) return null;
  if (verdict === 'pass') return 'pass';
  if (verdict === 'pass_with_observations') return 'pass_with_observations';
  if (isFailVerdict(verdict) || verdict === 'fail') return 'fail';
  if (isFlagVerdict(verdict)) return 'pass_with_observations';
  return null;
}

/**
 * SIL-1371 — the LISTING-level verdict family, from a verified buyer's
 * window-scoped per-node counts (`VerifiedEvidence.verificationSummary`).
 *
 * This is an AGGREGATION, not a second copy of `verificationVerdictFamily`: the
 * counts arriving here were already classified by prefix in SQL
 * (`listing_buyer_evidence`, migration 956, `FILTER (WHERE verdict LIKE
 * 'fail:%')`), so there is no raw classification left to normalise and this
 * function must never grow a verdict-string table of its own.
 *
 * WORST GOVERNS, and that is the whole point: a listing with one failed node
 * among four passing ones is a `fail` here, because the mark it gates is an
 * affirmative claim about the listing and a buyer cannot un-hear it by reading
 * the counts beside it (SIL-1371 — a `fail:thermal_throttle` node rendered
 * "Verified" with a green shield).
 *
 * `null` — every count zero — is NOT a pass. It means no node behind the
 * listing has a verdict in the window (or every run was an `invalid_run`, which
 * 956 counts in none of the three buckets), and a surface gating on this must
 * fail CLOSED there, exactly as `verificationVerdictFamily` returns `null`
 * rather than inventing an outcome for an unrecognised classification.
 */
export function verdictFamilyFromWindowCounts(counts: {
  pass: number;
  obs: number;
  fail: number;
}): VerificationVerdictFamily | null {
  if (counts.fail > 0) return 'fail';
  if (counts.obs > 0) return 'pass_with_observations';
  if (counts.pass > 0) return 'pass';
  return null;
}

/** SIL-613 — builds the two-check presentation shape from a raw run row.
 * See `VerificationChecksPresentation`'s doc comment for the `performed:
 * false` defaulting discipline. */
function buildChecksPresentation(run: VerificationRunForPresentation): VerificationChecksPresentation {
  const performed = run.checksPerformed ?? { compute: false, vram: false };
  return {
    compute: {
      performed: performed.compute,
      passed: performed.compute ? (run.computeCorrectness?.passed ?? false) : false,
    },
    vram: {
      performed: performed.vram,
      passed: performed.vram ? (run.vramTest?.passed ?? false) : false,
      bytesTested: performed.vram ? (run.vramTest?.bytesTested ?? 0) : 0,
    },
  };
}

/**
 * Synthesize the render-ready presentation state from an asset's recent
 * completed verification runs. `runs` needs only `results_ready` rows to
 * matter (others are ignored); order is irrelevant, this sorts by
 * `completedAt` descending itself.
 *
 * "Worst-run-governs" (§9 security finding 6) is deliberately NOT applied
 * here — that rule governs the confidence-tier BUMP (scoring/
 * verification-confidence.ts's `resolveVerificationConfidenceEffect`), a
 * separate concern this module never touches. Presentation always shows the
 * MOST RECENT completed run's outcome (with "passed on retry" context when
 * it reverses a recent fail) — the display and the scoring bump can
 * legitimately disagree (a display can show "passed on retry" while the
 * bump stays blocked if an earlier failure is still governing the TTL
 * window; that split is intentional, not a bug, and out of scope here).
 */
export function synthesizeVerificationPresentation(
  runs: readonly VerificationRunForPresentation[],
  now: Date,
): VerificationPresentation {
  const completed = runs
    .filter((r) => r.status === 'results_ready' && r.completedAt !== null)
    .slice()
    .sort((a, b) => b.completedAt!.getTime() - a.completedAt!.getTime());

  if (completed.length === 0) return { kind: 'none' };

  const latest = completed[0]!;
  const completedAtIso = latest.completedAt!.toISOString();
  const hardware = latest.hardware ?? null;

  // Tier 3 is ALWAYS this branch, regardless of pass/fail — the hard
  // anti-laundering requirement (§9/§11 finding 6): never shares a
  // discriminant, iconography, or copy with the tier12_* variants.
  if (latest.tier === 3) {
    return {
      kind: 'tier3_self_reported',
      passed: !isFailVerdict(latest.verdict) && !isFlagVerdict(latest.verdict),
      verdict: latest.verdict ?? 'invalid_run',
      completedAt: completedAtIso,
      checks: buildChecksPresentation(latest),
      hardware,
    };
  }

  if (isFailVerdict(latest.verdict)) {
    return {
      kind: 'tier12_failed',
      tier: latest.tier,
      classification: latest.verdict!,
      completedAt: completedAtIso,
      checks: buildChecksPresentation(latest),
      hardware,
    };
  }

  if (isFlagVerdict(latest.verdict)) {
    return {
      kind: 'tier12_flagged',
      tier: latest.tier,
      classification: latest.verdict!,
      completedAt: completedAtIso,
      checks: buildChecksPresentation(latest),
      hardware,
    };
  }

  // pass | pass_with_observations | invalid_run (treated as no qualifying
  // pass — invalid_run is neither a pass nor a fail:* classification, so it
  // falls through to 'none'-adjacent treatment for THIS asset's latest slot
  // only when nothing else in the window is presentable).
  if (latest.verdict !== 'pass' && latest.verdict !== 'pass_with_observations') {
    return { kind: 'none' };
  }

  const expiresAt = new Date(latest.completedAt!.getTime() + VERIFICATION_DISPLAY_TTL_DAYS * 24 * 60 * 60 * 1000);
  const stale = expiresAt.getTime() < now.getTime();

  const passedOnRetry = completed.slice(1).some((prior) => {
    if (prior.completedAt === null) return false;
    const gap = latest.completedAt!.getTime() - prior.completedAt.getTime();
    return gap >= 0 && gap <= GRACE_RETRY_WINDOW_MS && isFailVerdict(prior.verdict);
  });

  return {
    kind: 'tier12_pass',
    tier: latest.tier as 1 | 2,
    verdict: latest.verdict,
    completedAt: completedAtIso,
    stale,
    expiresAt: expiresAt.toISOString(),
    passedOnRetry,
    checks: buildChecksPresentation(latest),
    hardware,
  };
}

/**
 * SIL-688 — the honesty caveat string, or null when there's nothing to
 * caveat (no probe data, or a single-GPU host). Centralized here so every
 * renderer (harness display.ts, VerificationOutcome.tsx) shows the exact
 * same sentence, never a per-surface rewording.
 */
export function multiGpuCaveat(hardware: VerificationHardwarePresentation | null): string | null {
  if (!hardware || hardware.gpuCount <= 1) return null;

  // SIL-689 — a v9+ row carries a per-device `status` on every entry; once
  // every probed device was actually measured, the "only one of N was
  // tested" honesty caveat has nothing left to caveat and disappears
  // entirely (the per-card verdicts below ARE the honest disclosure now).
  // A pre-SIL-689 row (no `status` on any device — v8's single-adapter-match
  // shape) keeps the original "measured 1 of N" wording unchanged.
  const hasLoopStatus = hardware.devices.some((d) => d.status !== undefined);
  if (hasLoopStatus) {
    const measuredCount = hardware.devices.filter((d) => d.status === 'measured').length;
    if (measuredCount >= hardware.gpuCount) return null;
    return `This host reports ${hardware.gpuCount} GPUs. ${measuredCount} of ${hardware.gpuCount} were measured — the rest did not complete (see per-device detail below).`;
  }

  return hardware.measuredIndex === null
    ? `This host reports ${hardware.gpuCount} GPUs. WebGPU measured one of them; which one could not be determined.`
    : `This host reports ${hardware.gpuCount} GPUs. WebGPU measured 1 of ${hardware.gpuCount} (device #${hardware.measuredIndex}) — the others were not tested.`;
}

// ---------------------------------------------------------------------------
// SIL-613 — product copy, verbatim. Every renderer (passport §-section,
// listing embed, evidence-sources panel) MUST use these exact strings —
// they were reviewed and written deliberately to avoid overstating a pass,
// not to be reworded per surface.
//
// Presentation rules (hard requirements, not style choices):
//   1. No aggregate badge — this module never computes, and no caller may
//      compute, a combined pass/fail rollup across compute + VRAM.
//   2. Different weight for the two passes — a compute PASS may render with
//      a checkmark; a VRAM PASS must not (`vramPassLabel`/`vramFailLabel`
//      below always carry `{bytesTested}` inline in the PRIMARY label,
//      never a tooltip/footnote, precisely so a neutral, non-checkmark
//      treatment is the only way to render the sampled scope honestly).
//   3. Fails get identical full-weight treatment for both checks — the
//      asymmetry above is pass-vs-pass only.
//   4. Separate panels, compute first, each with its own heading and
//      co-located footnote — never merged into one "hardware health" card.
// ---------------------------------------------------------------------------

export const COMPUTE_CORRECTNESS_PASS_LABEL = 'Compute correctness: no errors found';
export const COMPUTE_CORRECTNESS_PASS_DETAIL =
  "Matrix-multiply output matched the known-correct answer on every checked pass for this run's duration.";
export const COMPUTE_CORRECTNESS_FAIL_LABEL = 'Compute correctness: arithmetic errors detected';
export const COMPUTE_CORRECTNESS_FAIL_DETAIL =
  'This GPU returned incorrect results without crashing or reporting a fault - the failure mode is silent data corruption.';
export const COMPUTE_CORRECTNESS_FOOTNOTE =
  "A pass means no incorrect results were observed during this run's checked passes. It is not a guarantee against failures outside this run's duration or workload.";
export const COMPUTE_CORRECTNESS_PANEL_HEADING = 'Compute Correctness';

export const VRAM_FOOTNOTE =
  "Browsers can only allocate a limited slice of a GPU's total memory - this test cannot see or measure total VRAM capacity, so the tested amount is not expressed as a percentage of the card. A pass covers only the {bytesTested} sampled and says nothing about the rest of the card's memory.";

/** `{bytesTested}` in every VRAM string is the human-formatted byte size
 * (`@siliconx/shared/ui/format`'s `formatBytes`) — callers interpolate it
 * themselves so this module stays dependency-free of the UI formatter; see
 * `VerificationOutcome.tsx` for the actual substitution. */
export function vramPassLabel(bytesTested: string): string {
  return `VRAM: no errors in ${bytesTested} tested`;
}
export function vramPassDetail(bytesTested: string): string {
  return `Zero, one, and pseudorandom patterns read back correctly in the ${bytesTested} sampled. Total VRAM capacity was not tested - a browser cannot allocate or see all of it.`;
}
export function vramFailLabel(bytesTested: string): string {
  return `VRAM: errors found in ${bytesTested} tested`;
}
export function vramFailDetail(bytesTested: string): string {
  return `Data written to the ${bytesTested} sampled did not read back correctly. This indicates memory corruption in the region tested.`;
}
export function vramFootnote(bytesTested: string): string {
  return VRAM_FOOTNOTE.replace('{bytesTested}', bytesTested);
}
/** The VRAM panel heading always carries the sampled size and is never
 * rendered as a bare "VRAM Test" (product review requirement, verbatim). */
export function vramPanelHeading(bytesTested: string): string {
  return `VRAM Pattern Test - ${bytesTested} sampled`;
}

export const COMPUTE_MISMATCH_BUYER_VERDICT =
  'This GPU produced incorrect arithmetic during a sustained compute stress test. The card returned wrong results without crashing. This is silent data corruption, not a performance issue, and it will affect any workload run on this GPU. Treat this as a hardware fault.';

export function vramMismatchBuyerVerdict(bytesTested: string): string {
  return `This GPU's memory failed a pattern test on ${bytesTested} sampled. Data written to that region did not read back correctly. Because the test only covers the region it could allocate, the true extent of the fault on this card is unknown - it may be larger than what was tested. Treat this as a hardware fault.`;
}
